import _ from 'lodash'
import queryString from 'query-string'

import peachApi from '../../api/peachApi' // Import the central peachApi

// Interfaces for options
type ExecuteRequestOptions = {
  token?: string // Will be ignored; peachApi gets this from authStore
  apiKey?: string // Will be ignored; peachApi gets this from authStore
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  pathPattern: string
  pathArgs?: { [key: string]: string | number | boolean } // Allow boolean for pathArgs
  headers?: { [key: string]: string }
  queryParams?: { [key: string]: any }
  body?: any
  apiBase?: string // If provided, this can be used to construct an absolute URL
  throwErrors?: boolean // peachApi will throw errors by default
  paginationUrl?: string
  responseType?: 'json' | 'text' // peachApi/rawApiCall handles response parsing
}

type GetPathOptions = {
  throwErrors?: boolean
}

const defaultHeaders = {
  'Content-type': 'application/json',
}

// Helper to construct the path from a pattern and arguments
const getPath = (
  pathPattern: string,
  pathArgs: { [key: string]: any } = {},
  opts: GetPathOptions = {},
): string => {
  const { throwErrors } = opts

  if (throwErrors && !pathPattern) {
    throw new Error('request requires a pathPattern')
  }

  const finalPath = _.reduce(
    pathArgs,
    (path, value, name) => {
      const subStr = `{${name}}`
      if (path.includes(subStr)) {
        if (value === undefined || value === null || value === '') {
          // Check for empty equivalent values
          if (throwErrors) {
            throw new Error(
              `path replacement '${name}' requires non-empty string value for path pattern '${pathPattern}'.`,
            )
          }
          return path.replace(subStr, ` [ ${name} ] `) // Keep placeholder for debugging
        }
        return path.replace(subStr, String(value)) // Convert value to string for replacement
      }
      return path
    },
    pathPattern,
  )

  if (finalPath.includes('{')) {
    const errorMessage = `Path arguments were not supplied for all placeholders in pathPattern '${pathPattern}', received '${JSON.stringify(
      pathArgs,
    )}'. Resulting path: ${finalPath}`
    if (throwErrors) {
      throw new Error(errorMessage)
    } else {
      // console.warn(errorMessage + ". Defaulting remaining path params to ID-XXXX-XXXX");
      return finalPath.replaceAll(/{\\w+}/g, 'ID-XXXX-XXXX')
    }
  }
  return finalPath
}

// Helper to create a query string from params object
const getQueryStringFromParams = (params?: {
  [key: string]: any
}): string | undefined => {
  if (_.isEmpty(params)) {
    return undefined
  }
  // Pass an empty object if params is undefined, or params itself if it exists.
  return queryString.stringify(params ?? {})
}

// Helper to construct a URL, ensuring no double slashes if base ends with / and route starts with /
const constructUrl = (
  base?: string,
  route?: string,
  queryStr?: string,
): string => {
  let fullPath = route || ''
  if (base && !fullPath.startsWith('http')) {
    const normalizedBase = base.endsWith('/') ? base.slice(0, -1) : base
    const normalizedRoute = fullPath.startsWith('/') ? fullPath : `/${fullPath}`
    fullPath = normalizedBase + normalizedRoute
  }
  return fullPath + (queryStr ? `?${queryStr}` : '')
}

const getBodyParam = (body: any, method: string) => {
  if (body && ['PUT', 'POST', 'PATCH'].includes(method)) {
    return body
  }
  return undefined
}

const executeRequest = (options: ExecuteRequestOptions) => {
  const {
    method,
    pathPattern,
    pathArgs,
    headers: customHeadersFromOptions,
    queryParams,
    body,
    apiBase: explicitApiBase,
    paginationUrl,
    apiKey,
  } = options

  let urlForPeachApi: string

  if (paginationUrl) {
    // If paginationUrl is provided, it's treated as the absolute URL
    urlForPeachApi = paginationUrl
  } else {
    const relativePath = getPath(pathPattern, pathArgs, {
      throwErrors: options.throwErrors,
    })
    const qs = getQueryStringFromParams(queryParams)

    if (explicitApiBase && !relativePath.startsWith('http')) {
      // If an explicitApiBase is given AND the path is relative, construct an absolute URL
      urlForPeachApi = constructUrl(explicitApiBase, relativePath, qs)
    } else {
      // Otherwise, pass the path as is (it might be absolute, or relative for peachApi to handle)
      urlForPeachApi = constructUrl(undefined, relativePath, qs)
    }
  }

  const requestBody = getBodyParam(body, method)

  // Start with customHeadersFromOptions
  const combinedHeaders = { ...customHeadersFromOptions }

  // If an apiKey is explicitly passed in options (e.g., for /auth/permissions),
  // add it as X-API-KEY. This will take precedence in peachApiCall if it checks headers first.
  if (apiKey) {
    combinedHeaders['X-API-KEY'] = apiKey
  }

  // If body is an object and Content-Type is not set, add application/json.
  if (
    requestBody &&
    typeof requestBody === 'object' &&
    !combinedHeaders['Content-Type'] &&
    !combinedHeaders['content-type']
  ) {
    combinedHeaders['Content-Type'] = defaultHeaders['Content-type']
  }

  // These options will be passed to peachApi.get, .post, etc.
  // peachApi methods spread these options into peachApiCall.
  const apiOptions = {
    url: urlForPeachApi,
    method, // Though redundant as we call specific peachApi.get/post, peachApiCall uses it
    headers: combinedHeaders,
    body: requestBody,
  }

  // Delegate to the appropriate peachApi method
  // peachApi methods return the parsed response body on success or throw a structured error.
  switch (method) {
    case 'GET':
      return peachApi.get(apiOptions)
    case 'POST':
      return peachApi.post(apiOptions)
    case 'PUT':
      return peachApi.put(apiOptions)
    case 'PATCH':
      return peachApi.patch(apiOptions)
    case 'DELETE':
      return peachApi.delete(apiOptions)
    default:
      // This case should ideally not be reached if `method` is correctly typed
      throw new Error(`Unsupported HTTP method: ${options.method}`)
  }
}

// The old `getFetchOptions` is no longer how requests are made or headers are added.
// Exporting it might be confusing or lead to incorrect usage.
// We'll remove it. If any part of the application was using `executeRequest.getFetchOptions`,
// that usage will need to be re-evaluated.
// Object.assign(executeRequest, { getFetchOptions }); // Remove this pattern

export default executeRequest
